<template>
  <z-form ref="form" @submit.native.prevent="sendFile">
    <transition name="fade" mode="out-in">
      <base-modal key="form">
        <template v-if="is_init">
          <template v-if="is_show_uploader && !dbLoaded">
            <vue-dropzone
              id="customdropzone"
              ref="file-upload"
              :options="dropzoneOptions"
              :include-styling="false"
              :use-custom-slot="true"
              @vdropzone-file-added="loadFile"
              @vdropzone-mounted="clearQuery"
              @vdropzone-success="fileUploaded"
              @vdropzone-error="fileUploadError"
            >
              <div class="ivu-upload ivu-upload-drag">
                <div
                  v-if="file_preview.name"
                  class="dnd-uploader__drop-zone flex flex--center flex--row-center"
                >
                  <icon type="md-document" size="52" class="drop-zone__icon mr-2" />
                  <div class="text-cut file-name">{{ file_preview.name }}</div>
                </div>

                <div v-else class="dnd-uploader__drop-zone">
                  <div v-if="is_loading" class="drop-zone__loader">
                    <spin size="large" />
                  </div>

                  <icon
                    v-if="!is_loading"
                    type="ios-cloud-upload"
                    size="52"
                    class="drop-zone__icon"
                  />

                  <p class="text-center text-muted">
                    {{ $t('shared.component.file_upload.drag') }}
                  </p>
                </div>
              </div>
            </vue-dropzone>

            <p v-if="!dbLoaded" class="text-muted mb-0 mt-3">
              {{ $t('shared.component.file_upload.format') }}: {{ format_user }}
            </p>
            <p v-if="!dbLoaded" class="text-muted mb-0">
              {{ $t('db.recipient.modal.import.required_format') }}
            </p>
            <p class="text-muted mb-0">
              {{ $t('shared.component.file_upload.file_example') }}
              <a
                href="https://drive.google.com/file/d/1099SCvrfOHh2IMB7C5ieYfqnHv9ddEVy"
                target="_blank">{{ $t('shared.component.file_upload.template') }}</a>
            </p>
          </template>

          <i-button
            v-if="!dbLoaded"
            slot="footer-ok"
            type="success"
            size="large"
            html-type="submit"
            :disabled="!file_added"
            :loading="is_button_loading"
          >
            {{
              isFileLoaded
                ? $t('shared.component.button.add')
                : $t('shared.component.button.upload')
            }}
          </i-button>
        </template>
        <template v-if="dbLoaded" #footer-cancel="{ close }">
          <i-button size="large" @click="close()">
            {{ $t('shared.component.button.close') }}
          </i-button>
        </template>
        <select-fields
          v-if="isFileLoaded && !dbLoaded"
          :fields="upload_list"
          @selectedFields="selectedFields"
        />
        <div v-if="dbLoaded" class="text-center">
          {{ $t('db.recipient.modal.import.imported') }}
        </div>
      </base-modal>
    </transition>
  </z-form>
</template>

<script>
  import BaseModal from '@shared/components/modal/modal';
  import SelectFields from './recipients-import-fields';
  import vue2Dropzone from 'vue2-dropzone';
  import 'vue2-dropzone/dist/vue2Dropzone.min.css';
  import { RecipientService } from '@db/services/RecipientService';
  import { API_URL } from '@shared/helpers/env';
  import { mapActions } from 'vuex';

  const formats = ['csv', 'xls', 'xlsx'];

  export default {
    components: {
      vueDropzone: vue2Dropzone,
      BaseModal,
      SelectFields
    },

    props: {
      database_id: {
        type: [Number, String],
        required: true
      },
      token: {
        type: String,
        required: true,
        default: ''
      }
    },

    data: function () {
      return {
        is_loading: false,
        file_preview: {},
        accept: formats.map((i) => `.${i}`).join(','),
        format_user: formats.map((i) => `.${i}`).join(', '),
        format: formats,
        file: null,
        file_id: Number,
        file_added: false,
        isFileLoaded: false,
        states: {
          success: 'success',
          error: 'error'
        },
        is_button_loading: false,
        dropzoneOptions: {
          url: `${API_URL}/api/v2/databases/import/files`,
          maxFilesize: 1,
          maxFiles: 1,
          headers: { Authorization: `Bearer ${this.token}` },
          acceptedFiles: '.csv, .xls, .xlsx'
        },
        showUploader: false,
        upload_list: {},
        selected_fields: {},
        dbLoaded: false,
        self: this
      };
    },

    computed: {
      is_init() {
        return !(this.is_success || this.is_error);
      },
      is_success() {
        return this.state === this.states.success;
      },
      is_error() {
        return this.state === this.states.error;
      },
      is_show_uploader() {
        return this.token;
      }
    },

    methods: {
      ...mapActions('db/databases/show/recipients', ['getAsyncData']),
      ...mapActions('db/databases/import/recipients', [
        'importRecipients',
        'importRecipientsTotal'
      ]),

      selectedFields(items) {
        this.selected_fields = items;
      },
      clearQuery() {
        this.file = null;
      },

      loadFile(file) {
        const max_file_size = 11 * 1000 * 1000;
        const valid_file = formats.filter((format) => file.name?.includes(`.${format}`))?.length > 0;

        if (file.size < max_file_size && valid_file) {
          this.file_preview = file;
          this.file = file;
        } else if (file.size > max_file_size) {
          const warning_msg = this.$t('core.document.error.max_size', { filename: file.name });
          this.$noty.warning(warning_msg);
        } else if (!valid_file) {
          this.$noty.warning(this.$t('shared.component.file_upload.invalid_format'));
        } else {
          this.file = null;
        }
      },

      fileUploadError(error) {
        if (error?.xhr?.status == 422) {
          if (typeof error?.xhr?.response === 'string') {
            const file_errors = JSON.parse(error.xhr.response).errors.file;

            file_errors.map((i) => this.$noty.error(i.message));
          }
        } else {
          return this.$noty.server_error(error);
        }
      },

      fileUploaded(...params) {
        this.file_id = Number(params[1].file_id);
        this.file_added = params[1].success;
      },

      getImportData() {
        RecipientService.getImportData(this.database_id, this.file_id)
          .then((response) => {
            this.importRecipients(response.file_content.splice(0, 5));
            this.importRecipientsTotal(response.total);
            this.$refs.form.$el[0].click();
            this.$router.push({
              name: 'db.database.import',
              params: { database_id: response.db.id, file_id: response.file_id }
            });
            // this.isFileLoaded = true;
          })
          .catch((error) => console.log(error));
      },

      saveImportData() {
        const params = {
          columns: this.selected_fields,
          ignore_header: false
        };

        RecipientService.saveImport(this.database_id, this.file_id, params)
          .then(() => {
            this.self.dbLoaded = true;

            /**
             * TODO :: Продумать реализацию запроса по готовности импорта
             */

            setTimeout(() => {
              this.getAsyncData();
            }, 4000);
          })
          .catch((error) => {
            this.$noty.server_error(error);
          });
      },

      sendFile() {
        if (!this.isFileLoaded) {
          this.getImportData();
        } else {
          this.saveImportData();
        }
      }
    }
  };
</script>

<style lang="scss">
  #customdropzone {
    // color: #757575;
    // background-color: #f9fafc;
    // height: 40px;
    // border-radius: 3px;
    // border: solid 1px #dddddd;
    // margin-bottom: 10px;
    // padding: 0 15px;
    // overflow: hidden;

    .dz-filename,
    .dz-error-message {
      display: none;
    }
    .dz-preview {
      // width: 100%;
      .dz-size,
      .dz-image,
      .dz-success-mark,
      .dz-error-mark,
      .dz-remove {
        display: none;
      }

      // .dz-details {
      //   height: 38px;
      //   display: flex;
      //   align-items: center;
      // }
    }
  }

  .dz-default-message {
    height: 38px;
    display: flex;
    align-items: center;
    user-select: none;
  }
  .dnd-uploader {
    &__drop-zone {
      min-height: 114px;
      padding: 20px 0;

      .text-center {
        margin: 0 5px;
      }
    }
  }

  .drop-zone__loader {
    height: 52px;
    width: 52px;
    margin: 0 auto;
  }

  .drop-zone__icon {
    color: $primary;
  }
</style>
